import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { unwrapDataResponse } from '@wport/core';
import { enterprisePatch } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { readJsonObject } from '../../../lib/io-helpers';
import { printJson } from '../../../lib/output';
import type { CreatedJob } from './create';
import { requireEncId, type WriteCtx } from './write-shared';

interface UpdateFlags {
	file?: string;
	idempotencyKey?: string;
	ifMatch?: string;
}

/**
 * `jobs update` 核心：讀 --file/stdin 的 partial JSON → PATCH /jobs/:enc_id（200）。
 * --if-match 帶目標 updated_at → 樂觀鎖（版本不符後端回 409）。
 */
export async function runJobsUpdate(
	ctx: WriteCtx,
	apiKey: string,
	encId: string,
	source: string,
	idempotencyKey: string,
	ifMatch?: string
): Promise<void> {
	const trimmed = requireEncId(encId);
	const jobBody = readJsonObject(source, { timeoutMs: ctx.timeoutMs });
	const { body } = await enterprisePatch(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		`/jobs/${encodeURIComponent(trimmed)}`,
		jobBody,
		{ idempotencyKey, ifMatch }
	);
	const updated = unwrapDataResponse<CreatedJob>(body);
	if (ctx.format === 'json') {
		printJson(updated);
		return;
	}
	process.stdout.write(`Updated job: ${updated.enc_id ?? trimmed}\n`);
}

export function registerEnterpriseJobsUpdate(parent: Command): void {
	parent
		.command('update <enc_id>')
		.description('Update a job posting from a JSON file (partial; use "-" for stdin)')
		.requiredOption('--file <path>', 'path to a partial JSON job body, or "-" for stdin')
		.option('--if-match <updated_at>', "optimistic lock: the job's current updated_at (409 if stale)")
		.option('--idempotency-key <key>', 'reuse across retries (default: a fresh UUID)')
		.action(async (encId: string, flags: UpdateFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runJobsUpdate(ctx, key, encId, flags.file as string, flags.idempotencyKey ?? randomUUID(), flags.ifMatch);
		});
}
